import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Instagram, Youtube, Facebook, Mail, ArrowRight, Music, Phone, Shield } from 'lucide-react';
import { Button } from "@/components/ui/button";

const Footer = () => {
  const navigate = useNavigate();

  const handleWorkWithUs = () => {
    navigate('/contact');
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-[#050816] text-[#F9FAFB] border-t border-[#111827]">
      <div className="container mx-auto px-6 pt-8 pb-16 md:pt-12 md:pb-24 max-w-[1200px]">
        
        {/* Top Row: 3 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 mb-16">
          
          {/* Column 1: Brand & Bio */}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png" 
                alt="RC Studios" 
                className="h-12 w-auto brightness-0 invert" 
              />
            </Link>
            <p className="text-[#9CA3AF] leading-relaxed text-sm md:text-base max-w-sm">
              RCSTUDIOS LLC is a creative media studio specializing in real estate photography, lifestyle, and brand content. From cinematic video to 3D tours and social media packages, we help you turn bold ideas into stunning visuals.
            </p>
            <div className="space-y-2 pt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#F5C056]">Contact us</p>
              <ul className="space-y-1 text-sm text-[#9CA3AF]">
                <li>
                  <a href="tel:+19285306604" className="hover:text-white transition-colors flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    (928) 530-6604
                  </a>
                </li>
                <li>
                  <a href="mailto:marketing@rcstudios.net" className="hover:text-white transition-colors">
                    Email: marketing@rcstudios.net
                  </a>
                </li>
                <li>Based in Kingman, Arizona</li>
                <li className="text-[#F5C056]/80">Serving Kingman, Bullhead City, Lake Havasu & Mohave County</li>
              </ul>
            </div>
          </div>

          {/* Column 2: Services */}
          <div className="md:pl-8">
            <h3 className="text-lg font-semibold mb-6 text-white">Services</h3>
            <ul className="space-y-4 text-[#9CA3AF]">
              <li>
                <Link to="/pricing/real-estate" className="hover:text-[#F5C056] transition-colors duration-200">
                  Real Estate Photography
                </Link>
              </li>
              <li>
                <Link to="/services/aerial-photos-video" className="hover:text-[#F5C056] transition-colors duration-200">
                  Drone & Aerial Media
                </Link>
              </li>
              <li>
                <Link to="/pricing/video-brand" className="hover:text-[#F5C056] transition-colors duration-200">
                  Brand & Social Content
                </Link>
              </li>
              <li>
                <Link to="/services/website-design" className="hover:text-[#F5C056] transition-colors duration-200">
                  Website & Platform Development
                </Link>
              </li>
              <li>
                <Link to="/services/ai-systems-automation" className="hover:text-[#F5C056] transition-colors duration-200">
                  AI Systems & Automation
                </Link>
              </li>
              <li>
                <Link to="/services/digital-persona-development" className="hover:text-[#F5C056] transition-colors duration-200">
                  AI Content & Digital Personas
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Owner Info & Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">About</h3>
            
            {/* Owner Info */}
            <div className="mb-6">
              <p className="text-white font-medium">Gregory Agri</p>
              <p className="text-sm text-[#9CA3AF]">Owner & Lead Photographer</p>
              <div className="flex items-center gap-2 mt-2 text-sm text-[#9CA3AF]">
                <Shield className="w-4 h-4 text-[#F5C056]" />
                <span>Fully Insured (Progressive liability)</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4 mb-8">
              <Button 
                asChild
                className="w-full bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 font-medium rounded-lg h-12"
                data-testid="footer-book-shoot-btn"
              >
                <Link to="/book" data-testid="footer-book-shoot-link">Book a Shoot</Link>
              </Button>
              <a 
                href="tel:+19285306604" 
                className="block text-center text-sm text-[#9CA3AF] hover:text-white transition-colors"
              >
                Call (928) 530-6604
              </a>
            </div>

              <div className="flex gap-5">
                <a href="https://www.instagram.com/overlandishadventures/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F5C056] transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.youtube.com/@rcstudios" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F5C056] transition-colors">
                  <Youtube className="h-5 w-5" />
                </a>
                <a href="https://www.facebook.com/rcstudios.media" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F5C056] transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="mailto:marketing@rcstudios.net" className="text-white hover:text-[#F5C056] transition-colors">
                  <Mail className="h-5 w-5" />
                </a>
              </div>
          </div>

        </div>

        {/* Divider */}
        <div className="border-t border-[#F5C056]/20 my-12"></div>

        {/* Bottom Row: Tags & Copyright */}
        <div className="flex flex-col items-center space-y-8">
          
          {/* Tags */}
          <div className="w-full flex flex-col items-center space-y-4">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-widest">Popular topics</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Real Estate Media",
                "Aerial & Drone",
                "Virtual Tours",
                "Social Content",
                "Brand Storytelling",
                "YouTube & Reels",
                "Arizona Properties",
                "Luxury Listings"
              ].map((tag) => (
                <span 
                  key={tag} 
                  className="px-4 py-1.5 rounded-full bg-[#111827] border border-[#F5C056]/40 text-xs text-white hover:border-[#F5C056] transition-colors cursor-default"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Copyright & Legal Links */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-4 text-xs text-[#9CA3AF]">
              <Link to="/privacy-policy" className="hover:text-[#F5C056] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-[#F5C056]/40">·</span>
              <Link to="/contact" className="hover:text-[#F5C056] transition-colors">
                Contact
              </Link>
            </div>
            <p className="text-xs text-[#9CA3AF] text-center">
              © 2026 RCSTUDIOS LLC. All rights reserved.
            </p>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
